import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

const slides = [
  {
    headline: "Complete Security Solutions",
    sub: "Surveillance & Body-Worn Camera Systems",
    tag: "Hytera Body-Worn Cameras",
    video: "https://cdn.pixabay.com/video/2022/08/04/127020-735826614_large.mp4",
    poster: "https://readdy.ai/api/search-image?query=professional%20security%20surveillance%20command%20center%20multiple%20monitors%20CCTV%20footage%20dark%20dramatic%20cinematic%20atmosphere%20high%20tech%20control%20room%20no%20people&width=1400&height=800&seq=hero-sec-poster&orientation=landscape",
  },
  {
    headline: "Rugged Devices for Every Environment",
    sub: "Mobile Computing for Field & Frontline Teams",
    tag: "Honeywell · Dell Rugged Pro",
    video: "https://cdn.pixabay.com/video/2022/03/14/110826-688095571_large.mp4",
    poster: "https://readdy.ai/api/search-image?query=industrial%20construction%20site%20golden%20hour%20dramatic%20sky%20wide%20aerial%20perspective%20cranes%20heavy%20equipment%20powerful%20atmosphere%20warm%20amber%20tones%20cinematic%20no%20people&width=1400&height=800&seq=hero-rug-poster&orientation=landscape",
  },
  {
    headline: "Enterprise Data Protection",
    sub: "Backup, Recovery & Storage Infrastructure",
    tag: "Arcfra · ExaGrid · Synology",
    video: "https://cdn.pixabay.com/video/2022/10/13/134874-762573379_large.mp4",
    poster: "https://readdy.ai/api/search-image?query=modern%20clean%20data%20center%20rows%20of%20server%20racks%20glowing%20green%20white%20LED%20lights%20cool%20tones%20dramatic%20perspective%20ultra%20wide%20shot%20no%20people%20professional%20architectural%20photography&width=1400&height=800&seq=hero-data-poster&orientation=landscape",
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setFading(false);
      }, 600);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const handleDotClick = (i: number) => {
    if (i === current) return;
    setFading(true);
    setTimeout(() => {
      setCurrent(i);
      setFading(false);
    }, 400);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[720px] overflow-hidden bg-[#0a0a0a]">

      {/* Video layers — one per slide, crossfade between them */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <video
            className="w-full h-full object-cover object-center"
            autoPlay
            muted
            loop
            playsInline
            poster={slide.poster}
          >
            <source src={slide.video} type="video/mp4" />
          </video>
        </div>
      ))}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20 z-10"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10"></div>

      {/* Content */}
      <div className="relative z-20 h-full flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
        <div className="max-w-2xl">

          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-white/60 mb-4">
            Authorized Distributor &amp; Technology Partner · Malaysia
          </span>

          {/* Slide text — fades on transition */}
          <div
            className="transition-all duration-500"
            style={{ opacity: fading ? 0 : 1, transform: fading ? "translateY(8px)" : "translateY(0)" }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-3">
              {slides[current].headline}
            </h1>
            <p className="text-lg md:text-2xl text-white/70 mb-2 font-light">
              {slides[current].sub}
            </p>
            <p className="text-xs font-semibold text-white/35 uppercase tracking-widest mb-8">
              {slides[current].tag}
            </p>
          </div>

          <p className="text-white/65 text-sm md:text-base max-w-lg mb-6 leading-relaxed">
            Authorized distributor for <strong className="text-white">Hytera, Arcfra &amp; ExaGrid</strong> — and technology partner for Synology, Honeywell &amp; Dell Rugged Pro — delivering fully integrated solutions across Malaysia.
          </p>

          {/* Ecosystem Badge Strip */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-8">
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-verified-badge-fill text-white text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">Authorized Distributor</p>
                <p className="text-xs font-semibold text-white leading-none">Hytera &middot; Arcfra &middot; ExaGrid</p>
              </div>
            </div>
            <div className="hidden sm:flex w-px h-8 bg-white/20"></div>
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-shake-hands-fill text-white/70 text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">Technology Partner</p>
                <p className="text-xs font-semibold text-white leading-none">Synology &middot; Honeywell &middot; Dell Rugged Pro</p>
              </div>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <Link
              to="/solutions"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 w-fit"
            >
              Solutions Overview
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact"
              className="border border-white/60 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer w-fit"
            >
              Contact Us Now
            </Link>
          </div>

          {/* Quick-Nav Pills */}
          <div className="flex flex-wrap gap-2">
            <span className="text-white/40 text-xs self-center mr-1 hidden sm:inline">Quick access:</span>
            {[
              { to: "/hytera-bwv", icon: "ri-camera-3-line", label: "Hytera BWC" },
              { to: "/arcfra-spotlight", icon: "ri-server-line", label: "Arcfra HCI" },
              { to: "/exagrid-spotlight", icon: "ri-hard-drive-2-line", label: "ExaGrid Backup" },
              { to: "/rugged-spotlight", icon: "ri-smartphone-line", label: "Rugged Devices" },
              { to: "/solutions/surveillance", icon: "ri-shield-check-line", label: "Surveillance" },
            ].map((pill) => (
              <Link
                key={pill.to}
                to={pill.to}
                className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-medium px-3.5 py-2 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`${pill.icon} text-xs`}></i>
                {pill.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-6 lg:left-20 flex items-center gap-3 z-20">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            className={`transition-all duration-300 cursor-pointer rounded-full ${
              i === current ? "w-8 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
        <span className="text-white/30 text-[10px] font-medium ml-2 hidden sm:inline">
          {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-8 lg:right-20 z-20 flex flex-col items-center gap-1 text-white/50 text-xs">
        <i className="ri-arrow-down-line animate-bounce text-base"></i>
        <span>Scroll</span>
      </div>
    </section>
  );
}
